import React, { useState, useRef, useEffect } from 'react';
import { Button, Loader, Textarea, NumberInput, Slider, Text, Group, Stack, Tabs, Collapse } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faUpload, 
  faMicrophone, 
  faMusic, 
  faWandMagicSparkles, 
  faPlay, 
  faPause,
  faVolumeHigh,
  faDownload,
  faRefresh,
  faCheck,
  faChevronDown,
  faChevronUp,
  faLightbulb,
  faCopy
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../context/NotificationContext';
import { projectService, PromptProject } from '../../services/projectService';
import { PromptBrowser } from '../shared';
import { useAuth } from '../../context/Auth/AuthContext';
import { ElevenLabsService } from './services/elevenLabsService';
import { VoiceSelector, VoiceSettings, MusicGenerationTab } from './components';
import type { MusicGenerationParams } from './components/MusicGenerationTab';
import { ElevenLabsVoice, ElevenLabsVoiceSettings, GenerateNarrationRequest, GenerateNarrationResponse } from './types';
import { API_BASE_URL } from '../../config/api';
import { isAdminUser } from '../../config/environment';
import { downloadAudio } from '../../services/autoDownloadService';
import styles from './AudioModal.module.css';
import CustomSlider from './CustomSlider';
import i18n from '../../i18n';

interface AudioModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAudioGenerated: (audioUrl: string, projectId: string, type: 'narration' | 'prompts' | 'melody' | 'music') => void;
  type: 'upload' | 'narration' | 'prompts' | 'melody' | 'music';
  currentProjectId?: string; // Renamed to avoid conflict with internal projectId state
  hideAllPromptsTab?: boolean; // New prop to hide "All Prompts" tab
}

interface GenerateMelodyRequest {
  prompt: string;
  duration?: number;
  temperature?: number;
  guidance_scale?: number;
  seed?: number;
  projectId?: string;
}

interface GenerateMelodyResponse {
  success: boolean;
  message: string;
  data: {
    projectId: string;
    prompt: string;
    melodies: Array<{
      index: number;
      seed: number;
      s3Url: string;
      duration: number;
    }>;
    count: number;
  };
}

// ElevenLabs model options
const ELEVENLABS_MODELS = [
  { value: 'eleven_multilingual_v2', label: 'Multilingual V2 (Recommended)', description: 'Best quality, supports multiple languages' },
  { value: 'eleven_flash_v2_5', label: 'Flash V2.5', description: 'Faster generation, good quality' },
  { value: 'eleven_conversational', label: 'Conversational', description: 'Optimized for conversational content' },
];

// Ambient songs for narrative (keeping melody generation compatibility)
const AMBIENT_SONGS = [
  { value: 'gentle_piano', label: 'Gentle Piano' },
  { value: 'soft_strings', label: 'Soft Strings' },
  { value: 'nature_sounds', label: 'Nature Sounds' },
  { value: 'peaceful_harp', label: 'Peaceful Harp' },
  { value: 'meditation_bells', label: 'Meditation Bells' },
  { value: 'none', label: 'No Ambient Music' },
];

// Custom Select Component
interface CustomSelectProps {
  data: Array<{ value: string; label: string; description?: string }>;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ data, value, onChange, placeholder }) => {
  const { t } = useTranslation('audioModal');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const selectRef = useRef<HTMLDivElement>(null);
  
  const selectedItem = data.find(item => item.value === value);
  const filteredData = data.filter(item => 
    item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={styles.customSelect} ref={selectRef}>
      <div 
        className={styles.selectTrigger} 
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={styles.selectValue}>
          {selectedItem ? selectedItem.label : placeholder || t('customSelect.placeholder')}
        </span>
        <FontAwesomeIcon 
          icon={faChevronDown} 
          className={`${styles.selectArrow} ${isOpen ? styles.selectArrowOpen : ''}`}
        />
      </div>
      
      {isOpen && (
        <div className={styles.selectDropdown}>
          {data.length > 5 && (
            <input
              type="text"
              placeholder={t('customSelect.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.selectSearch}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <div className={styles.selectOptions}>
            {filteredData.map((item) => (
              <div
                key={item.value}
                className={`${styles.selectOption} ${item.value === value ? styles.selectOptionSelected : ''}`}
                onClick={() => handleSelect(item.value)}
              >
                <div className={styles.selectOptionLabel}>{item.label}</div>
                {item.description && (
                  <div className={styles.selectOptionDescription}>{item.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AudioModal: React.FC<AudioModalProps> = ({ 
  isOpen, 
  onClose, 
  onAudioGenerated, 
  type,
  currentProjectId,
  hideAllPromptsTab = false // Default to false to maintain existing behavior
}) => {
  const { t } = useTranslation('audioModal');
  const [activeStep, setActiveStep] = useState<'generation' | 'result'>('generation');
  const [activeTab, setActiveTab] = useState<'narration' | 'prompts' | 'melody' | 'music'>(
    type === 'prompts' ? 'prompts' : 
    type === 'melody' ? 'melody' : 
    type === 'music' ? 'music' : 
    'narration'
  );
  const [isGenerating, setIsGenerating] = useState(false);
  
  // ElevenLabs state
  const [voices, setVoices] = useState<ElevenLabsVoice[]>([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [voicesError, setVoicesError] = useState<string | null>(null);
  
  // Narration form state
  const [narrativeText, setNarrativeText] = useState('');
  const [selectedVoice, setSelectedVoice] = useState<string>('');
  const [selectedModel, setSelectedModel] = useState('eleven_multilingual_v2');
  const [voiceSettings, setVoiceSettings] = useState<ElevenLabsVoiceSettings>({
    stability: 0.5,
    similarity_boost: 0.75,
    style: 0.0,
    use_speaker_boost: true
  });
  const [ambientSong, setAmbientSong] = useState('none');
  
  // Melody form state
  const [melodyPrompt, setMelodyPrompt] = useState('');
  const [melodyDuration, setMelodyDuration] = useState(10);
  const [melodyTemperature, setMelodyTemperature] = useState(1.0);
  const [guidanceScale, setGuidanceScale] = useState(3.0);
  const [melodySeed, setMelodySeed] = useState<number | undefined>(undefined);
  
  // Music form state (NEW) - Single object for all params
  const [musicParams, setMusicParams] = useState<MusicGenerationParams>({
    prompt: '',
    musicLengthMs: 30000,
    mode: 'lyrics', // Default to lyrics mode (simple is hidden)
    includeVocals: true, // Default to true for lyrics mode
    language: 'english',
  });
  
  // File upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Result state
  const [generatedAudio, setGeneratedAudio] = useState<string | null>(null);
  const [generatedMelodies, setGeneratedMelodies] = useState<Array<{
    index: number;
    s3Url: string;
    duration: number;
    seed: number;
  }>>([]);
  const [selectedMelodyIndex, setSelectedMelodyIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [projectId, setProjectId] = useState<string>('');
  const [isPlayingPreview, setIsPlayingPreview] = useState<string | null>(null);
  
  // Accordion state
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  
  const { showNotification } = useNotification();
  const { token, user } = useAuth();

  // Load ElevenLabs voices when modal opens
  useEffect(() => {
    if (isOpen && voices.length === 0) {
      loadVoices();
    }
  }, [isOpen]);

  const loadVoices = async () => {
    setVoicesLoading(true);
    setVoicesError(null);
    
    try {
      const fetchedVoices = await ElevenLabsService.fetchVoices();
      setVoices(fetchedVoices);
      
      // Set default voice if none selected
      if (!selectedVoice && fetchedVoices.length > 0) {
        const defaultVoice = ElevenLabsService.getRecommendedVoice(fetchedVoices, 'general');
        if (defaultVoice) {
          setSelectedVoice(defaultVoice.id);
          // Set optimal voice settings based on voice
          if (defaultVoice.settings) {
            setVoiceSettings(defaultVoice.settings);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load voices:', error);
      setVoicesError('Failed to load voices. Please try again.');
      showNotification({
        title: 'Error',
        message: 'Failed to load voices',
        type: 'error'
      });
    } finally {
      setVoicesLoading(false);
    }
  };

  const handleVoicesUpdated = () => {
    loadVoices();
  };

  // Update active tab when modal opens with different type
  React.useEffect(() => {
    if (isOpen) {
      console.log('🎵 AudioModal opened with type:', type);
      const newTab = type === 'prompts' ? 'prompts' : 
                     type === 'music' ? 'music' :
                     type === 'melody' ? 'melody' :
                     'narration';
      console.log('🎵 Setting activeTab to:', newTab);
      setActiveTab(newTab);
      setActiveStep('generation');
    }
  }, [isOpen, type]);

  // Reset form when modal closes
  const handleClose = () => {
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
    setIsPlaying(false);
    setIsPlayingPreview(null);
    setActiveStep('generation');
    setNarrativeText('');
    setMelodyPrompt('');
    setGeneratedAudio(null);
    setGeneratedMelodies([]);
    setUploadedFile(null);
    setVoicesError(null);
    onClose();
  };

  // Handle using prompt for audio generation
  const handleLoadForAudio = (promptText: string, project: PromptProject) => {
    // Always load into narration field and switch to narration tab
    // since this is accessed from the "All Prompts" tab in the narration context
    setNarrativeText(promptText);
    setActiveTab('narration');
    
    // showNotification({
    //   title: 'Prompt Loaded',
    //   message: 'Prompt loaded for narration',
    //   type: 'success'
    // });
  };

  // Handle using prompt as melody description
  const handleUsePrompt = (prompt: string, project: PromptProject) => {
    setMelodyPrompt(prompt);
    setActiveTab('melody');
    // showNotification({
    //   title: 'Prompt Applied',
    //   message: 'Prompt has been applied to melody generation',
    //   type: 'success'
    // });
  };

  // Generate narration using ElevenLabs
  const generateNarration = async (): Promise<GenerateNarrationResponse> => {
    // Extract base language code (e.g., "en" from "en-US")
    // ElevenLabs only accepts: en, fr, es, de, it, pt, pl, nl, sv, cs, ru, ar, zh, ja, ko, hi, auto
    const baseLanguage = i18n.language.split('-')[0];
    
    const request: GenerateNarrationRequest = {
      text: narrativeText,
      elevenlabs_voice_id: selectedVoice,
      elevenlabs_model: selectedModel,
      language: baseLanguage, // Use base language code only (en, fr, etc.)
      stability: voiceSettings.stability,
      similarity_boost: voiceSettings.similarity_boost,
      style: voiceSettings.style,
      use_speaker_boost: voiceSettings.use_speaker_boost,
      projectId: currentProjectId,
    };

    return await ElevenLabsService.generateNarration(request);
  };

  // Generate melody
  const generateMelody = async (): Promise<GenerateMelodyResponse> => {
    const request: GenerateMelodyRequest = {
      prompt: melodyPrompt,
      duration: melodyDuration,
      temperature: melodyTemperature,
      guidance_scale: guidanceScale,
      seed: melodySeed,
      projectId: currentProjectId, // Pass current project ID to prevent creating new project
    };

    const response = await fetch(`${API_BASE_URL}/music-generation/melody`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  };

  // Generate music with ElevenLabs Music API
  const generateMusic = async () => {
    const request = {
      ...musicParams,
      projectId: currentProjectId,
    };

    const response = await fetch(`${API_BASE_URL}/music-generation/elevenlabs-music`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  };

  // Handle generation
  const handleGenerate = async () => {
    if (activeTab === 'narration' && !narrativeText.trim()) {
      showNotification({
        title: 'Missing Text',
        message: 'Please enter text for narration',
        type: 'error'
      });
      return;
    }

    if (activeTab === 'melody' && !melodyPrompt.trim()) {
      showNotification({
        title: 'Missing Prompt',
        message: 'Please enter a prompt for melody generation',
        type: 'error'
      });
      return;
    }

    if (activeTab === 'music' && !musicParams.prompt.trim()) {
      showNotification({
        title: 'Missing Description',
        message: 'Please describe the music you want to generate',
        type: 'error'
      });
      return;
    }

    setIsGenerating(true);

    try {
      if (activeTab === 'narration') {
        const response = await generateNarration();
        if (response.success) {
          setGeneratedAudio(response.data.s3Url);
          setProjectId(response.data.projectId);
          setActiveStep('result');
          // showNotification({
          //   title: 'Success',
          //   message: 'Narration generated successfully!',
          //   type: 'success'
          // });
        }
      } else if (activeTab === 'melody') {
        const response = await generateMelody();
        if (response.success) {
          setGeneratedMelodies(response.data.melodies);
          setProjectId(response.data.projectId);
          setActiveStep('result');
          // showNotification({
          //   title: 'Success',
          //   message: `Generated ${response.data.count} melodies successfully!`,
          //   type: 'success'
          // });
        }
      } else if (activeTab === 'music') {
        const response = await generateMusic();
        if (response.success) {
          setGeneratedAudio(response.data.s3Url);
          setProjectId(response.data.projectId);
          setActiveStep('result');
          // showNotification({
          //   title: 'Success',
          //   message: `Music generated successfully! Used ${response.data.creditsUsed} credits.`,
          //   type: 'success'
          // });
        }
      }
    } catch (error) {
      console.error('Generation failed:', error);
      showNotification({
        title: 'Generation Failed',
        message: `Failed to generate ${activeTab}. Please try again.`,
        type: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle voice preview playback
  const handleVoicePreview = (voiceId: string, audioUrl: string) => {
    // Stop current preview if playing
    if (currentAudio && !currentAudio.paused) {
      currentAudio.pause();
      setIsPlayingPreview(null);
      setCurrentAudio(null);
    }

    // If clicking the same voice that's playing, just stop
    if (isPlayingPreview === voiceId) {
      setIsPlayingPreview(null);
      return;
    }

    // Play the provided audio URL
    const audio = new Audio(audioUrl);
    audio.addEventListener('ended', () => {
      setIsPlayingPreview(null);
      setCurrentAudio(null);
    });
    
    audio.addEventListener('error', (e) => {
      console.error('Failed to load voice preview:', audioUrl, e);
      setIsPlayingPreview(null);
      setCurrentAudio(null);
      showNotification({
        title: 'Preview Error',
        message: 'Failed to load voice preview',
        type: 'error'
      });
    });
    
    audio.play().then(() => {
      setIsPlayingPreview(voiceId);
      setCurrentAudio(audio);
    }).catch(error => {
      console.error('Failed to play voice preview:', error);
      setIsPlayingPreview(null);
      setCurrentAudio(null);
    });
  };

  // Handle voice selection
  const handleVoiceSelect = (voiceId: string) => {
    setSelectedVoice(voiceId);
    
    // Update voice settings to recommended values for this voice
    const voice = ElevenLabsService.getVoiceById(voices, voiceId);
    if (voice?.settings) {
      setVoiceSettings(voice.settings);
    }
  };

  // Handle audio playback
  const handlePlayPause = (audioUrl: string) => {
    if (currentAudio && !currentAudio.paused) {
      currentAudio.pause();
      setIsPlaying(false);
      setCurrentAudio(null);
    } else {
      if (currentAudio) {
        currentAudio.pause();
      }
      
      const audio = new Audio(audioUrl);
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentAudio(null);
      });
      
      audio.play();
      setIsPlaying(true);
      setCurrentAudio(audio);
    }
  };

  // Handle file upload
  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      // Check file type - only allow audio files
      if (!file.type.startsWith('audio/')) {
        showNotification({
          title: 'Invalid File',
          message: 'Please select an audio file',
          type: 'error'
        });
        return;
      }
      
      setUploadedFile(file);
      // Here you would handle the upload logic
    }
  };

  // Handle regeneration
  const handleRegenerate = () => {
    setActiveStep('generation');
    setGeneratedAudio(null);
    setGeneratedMelodies([]);
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
    setIsPlaying(false);
  };

  // Handle completion
  const handleComplete = () => {
    const selectedAudioUrl = activeTab === 'narration' 
      ? generatedAudio 
      : generatedMelodies[selectedMelodyIndex]?.s3Url;
      
    if (selectedAudioUrl) {
      onAudioGenerated(selectedAudioUrl, projectId, activeTab);
    }
  };

  // Get voice sample URL (placeholder for now)
  const getVoiceSampleUrl = (voicePreset: string): string => {
    // This would be replaced with actual voice sample URLs
    return `${API_BASE_URL}/voice-samples/${voicePreset}.wav`;
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {type === 'upload' ? t('modal.title.upload') : 
             type === 'narration' ? t('modal.title.narration') :
             type === 'music' ? t('modal.title.music') :
             activeStep === 'generation' ? t('modal.title.narration') : t('result.title')}
          </h2>
          <button className={styles.closeButton} onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {activeStep === 'generation' ? (
          <>
            {type !== 'upload' && (
              <Tabs value={activeTab} onChange={(value) => setActiveTab(value as 'narration' | 'prompts' | 'music')}>
                <Tabs.List>
                  <Tabs.Tab value="narration">
                    <FontAwesomeIcon icon={faMicrophone} style={{ marginRight: '8px' }} />
                    {t('tabs.narration')}
                  </Tabs.Tab>
                  {isAdminUser(user) && (
                    <Tabs.Tab value="music">
                      <FontAwesomeIcon icon={faMusic} style={{ marginRight: '8px' }} />
                      {t('tabs.music')}
                    </Tabs.Tab>
                  )}
                  {!hideAllPromptsTab && (
                    <Tabs.Tab value="prompts">
                      <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '8px' }} />
                      {t('tabs.prompts')}
                    </Tabs.Tab>
                  )}
                </Tabs.List>

              <Tabs.Panel value="narration" pt="md">
                <Stack gap="md">
                  <div>
                    <Text size="sm" fw={500} mb="xs">{t('narration.textLabel')} *</Text>
                    <Textarea
                      placeholder={t('narration.textPlaceholder')}
                      value={narrativeText}
                      onChange={(e) => setNarrativeText(e.target.value)}
                      minRows={4}
                      maxRows={8}
                    />
                  </div>

                  <div>
                    <Text size="sm" fw={500} mb="xs">{t('narration.voiceLabel')}</Text>
                    {voicesError && (
                      <div className={styles.errorContainer}>
                        <span className={styles.errorMessage}>{voicesError}</span>
                        <button className={styles.retryButton} onClick={loadVoices}>
                          {t('narration.voiceRetry')}
                        </button>
                      </div>
                    )}
                    <VoiceSelector
                      voices={voices}
                      selectedVoice={selectedVoice}
                      onVoiceSelect={handleVoiceSelect}
                      onPreviewPlay={handleVoicePreview}
                      isPlayingPreview={isPlayingPreview}
                      loading={voicesLoading}
                      onVoicesUpdated={handleVoicesUpdated}
                    />
                  </div>

                  {/* Advanced Configuration Accordion */}
                  {/* <div className={styles.accordionContainer}>
                    <button
                      className={styles.accordionTrigger}
                      onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                      type="button"
                    >
                      <span className={styles.accordionTitle}>Voice Settings</span>
                      <FontAwesomeIcon 
                        icon={isAdvancedOpen ? faChevronUp : faChevronDown} 
                        className={styles.accordionArrow}
                      />
                    </button>
                    
                    <Collapse in={isAdvancedOpen}>
                      <div className={styles.accordionContent}>
                        <VoiceSettings
                          settings={voiceSettings}
                          onChange={setVoiceSettings}
                          disabled={voicesLoading}
                        />
                      </div>
                    </Collapse>
                  </div> */}
                </Stack>
              </Tabs.Panel>

              {/* MUSIC TAB PANEL */}
              <Tabs.Panel value="music" pt="md">
                <MusicGenerationTab
                  onParamsChange={setMusicParams}
                  initialParams={musicParams}
                />
              </Tabs.Panel>

              {!hideAllPromptsTab && (
                <Tabs.Panel value="prompts" pt="md">
                  <Stack gap="md" style={{ minHeight: '400px' }}>
                    <div>
                      <Text size="sm" fw={500} mb="xs">{t('prompts.title')}</Text>
                      <Text size="xs" color="dimmed" mb="md">
                        {t('prompts.description')}
                      </Text>
                    </div>

                    <PromptBrowser
                      onPromptSelect={handleUsePrompt}
                      onLoadForAudio={handleLoadForAudio}
                      showUseActions={true}
                      showSelectAction={false}
                      compact={true}
                      className={styles.modalPromptBrowser}
                      projectId={currentProjectId} // Use the renamed prop
                      height="350px"
                    />
                    
                    <div style={{ 
                      backgroundColor: 'rgba(59, 130, 246, 0.1)', 
                      padding: '12px', 
                      borderRadius: '8px',
                      border: '1px solid rgba(59, 130, 246, 0.2)'
                    }}>
                      <Text size="xs" color="dimmed">
                        💡 <strong>{t('prompts.tip')}</strong>
                      </Text>
                    </div>
                  </Stack>
                </Tabs.Panel>
              )}
            </Tabs>
            )}

            {type === 'upload' && (
              <div className={styles.uploadSection}>
                <div className={styles.uploadArea} onClick={handleFileUpload}>
                  <FontAwesomeIcon icon={faUpload} size="3x" className={styles.uploadIcon} />
                  <h3>{t('upload.title')}</h3>
                  <p>{t('upload.description')}</p>
                  {uploadedFile && (
                    <div className={styles.uploadedFile}>
                      <strong>{t('upload.selectedFile', { filename: uploadedFile.name })}</strong>
                    </div>
                  )}
                </div>
                
                <input
                  type="file"
                  accept="audio/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                
                {uploadedFile && (
                  <div className={styles.modalActions}>
                    <button onClick={handleClose} className={styles.cancelButton}>
                      {t('modal.close')}
                    </button>
                    <Button onClick={() => {/* Handle upload */}} className={styles.primaryButton}>
                      {t('result.uploadAndContinue')}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {type !== 'upload' && (
              <div className={styles.modalActions}>
                <button onClick={handleClose} className={styles.cancelButton}>
                  {t('modal.close')}
                </button>
                {activeTab !== 'prompts' && (
                  <Button 
                    onClick={handleGenerate} 
                    loading={isGenerating}
                    className={styles.primaryButton}
                  >
                    <FontAwesomeIcon icon={faWandMagicSparkles} style={{marginRight: "8px"}} />
                    {isGenerating ? t('narration.generating') : 
                      activeTab === 'narration' ? t('narration.generateButton') : 
                      t('narration.generateButton')}
                  </Button>
                )}
              </div>
            )}
          </>
        ) : (
          // Result step
          <div className={styles.resultSection}>
            {activeTab === 'narration' && generatedAudio ? (
              <div className={styles.audioResult}>
                <div className={styles.audioPlayer}>
                  <FontAwesomeIcon 
                    icon={faVolumeHigh} 
                    size="3x" 
                    className={styles.audioIcon} 
                  />
                  <h3>{t('result.generatedNarration')}</h3>
                  <div className={styles.audioControls}>
                    <Button
                      onClick={() => handlePlayPause(generatedAudio)}
                      className={styles.playButton}
                      leftSection={<FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />}
                    >
                      {isPlaying ? t('result.pause') : t('result.play')}
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          await downloadAudio(generatedAudio!, {
                            filename: 'narration.mp3',
                            onStart: () => {
                              console.log('📥 Starting narration download...');
                            },
                            onComplete: () => {
                              showNotification({
                                title: '✅ Downloaded',
                                message: 'Narration downloaded successfully',
                                type: 'success'
                              });
                            },
                            onError: (err) => {
                              showNotification({
                                title: '❌ Download Failed',
                                message: err.message || 'Failed to download narration',
                                type: 'error'
                              });
                            }
                          });
                        } catch (error) {
                          console.error('Download error:', error);
                        }
                      }}
                      variant="outline"
                      leftSection={<FontAwesomeIcon icon={faDownload} />}
                    >
                      {t('result.download')}
                    </Button>
                  </div>
                </div>
              </div>
            ) : activeTab === 'melody' && generatedMelodies.length > 0 ? (
              <div className={styles.melodyResults}>
                <h3>{t('result.melodyTitleNarration')}</h3>
                <p>{t('result.melodyDescription', { count: generatedMelodies.length })}</p>
                
                <div className={styles.melodyOptions}>
                  {generatedMelodies.map((melody, index) => (
                    <div 
                      key={melody.index}
                      className={`${styles.melodyOption} ${selectedMelodyIndex === index ? styles.selected : ''}`}
                      onClick={() => setSelectedMelodyIndex(index)}
                    >
                      <div className={styles.melodyHeader}>
                        <h4>{t('result.melodyNumber', { number: melody.index })}</h4>
                        <span className={styles.duration}>{melody.duration.toFixed(1)}s</span>
                      </div>
                      <div className={styles.melodyControls}>
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayPause(melody.s3Url);
                          }}
                          leftSection={<FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />}
                        >
                          {isPlaying ? t('result.pause') : t('result.play')}
                        </Button>
                        <Button
                          size="sm"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await downloadAudio(melody.s3Url, {
                                filename: `melody_${melody.index}.mp3`,
                                onStart: () => {
                                  console.log(`📥 Starting melody ${melody.index} download...`);
                                },
                                onComplete: () => {
                                  showNotification({
                                    title: '✅ Downloaded',
                                    message: `Melody ${melody.index} downloaded`,
                                    type: 'success'
                                  });
                                },
                                onError: (err) => {
                                  showNotification({
                                    title: '❌ Download Failed',
                                    message: err.message || 'Failed to download melody',
                                    type: 'error'
                                  });
                                }
                              });
                            } catch (error) {
                              console.error('Melody download error:', error);
                            }
                          }}
                          variant="outline"
                          leftSection={<FontAwesomeIcon icon={faDownload} />}
                        >
                          {t('result.download')}
                        </Button>
                      </div>
                      {selectedMelodyIndex === index && (
                        <div className={styles.selectedIndicator}>
                          <FontAwesomeIcon icon={faCheck} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className={styles.modalActions}>
              <button 
                onClick={handleRegenerate}
                className={styles.cancelButton}
              >
                <FontAwesomeIcon icon={faRefresh} style={{marginRight: "8px"}} />
                {t('result.regenerate')}
              </button>
              <Button 
                onClick={handleComplete}
                className={styles.primaryButton}
              >
                <FontAwesomeIcon icon={faCheck} style={{marginRight: "8px"}} />
                {t('result.complete')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioModal;